/* Pés Felizes — calculadora de tamanhos (<pf-size-finder>), usada na página e no pop-up da página de produto.
   Dados: /collections/all?view=pf-size-data (templates/collection.pf-size-data.liquid). Com data-only-current (página de produto)
   mostra só a recomendação para o produto aberto e não carrega nem sugere outros modelos. Regra de folga: por defeito ideal 0,8–1,2 cm e
   aceitável 0,5–0,7 cm; cada guia de tamanhos (metaobjeto size_guide) pode substituir estes valores.
   Sugestões de outros modelos: só entram tamanhos que o modelo tem mesmo (existe variante com esse tamanho) e que estão em stock
   (variant.a !== false). O guia de tamanhos é por marca e pode ter tamanhos que um modelo concreto não fabrica. Se o tamanho ideal
   não existe ou está esgotado, é sugerido o melhor tamanho disponível dentro da folga aceitável, e o modelo some se não houver nenhum. */
(() => {
  if (customElements.get('pf-size-finder')) return;

  const DEFAULTS = { idealMin: 0.8, idealMax: 1.2, warnMin: 0.5, target: 1.0 };
  const FOOT_MIN = 5;
  const FOOT_MAX = 35;
  const CACHE_KEY = 'pf-size-data-v1';
  const CACHE_TTL = 30 * 60 * 1000;
  const CONCURRENCY = 6;

  const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
  const fmt = (n) => n.toFixed(1).replace('.', ',');
  const round1 = (n) => Math.round(n * 10) / 10;

  const rulesFor = (guide) => {
    const idealMin = num(guide.imin) ?? DEFAULTS.idealMin;
    const idealMax = num(guide.imax) ?? DEFAULTS.idealMax;
    return {
      idealMin,
      idealMax,
      warnMin: num(guide.wmin) ?? DEFAULTS.warnMin,
      warnMax: num(guide.wmax) ?? idealMax,
      target: num(guide.tg) ?? DEFAULTS.target,
    };
  };

  /** Devolve o melhor tamanho de um guia para um comprimento de pé (cm), ou null.
   *  isAvailable(label) é opcional: se existir, tamanhos para os quais devolve false (inexistentes ou esgotados) são ignorados. */
  const recommend = (guide, foot, isAvailable) => {
    const rules = rulesFor(guide);
    let best = null;
    for (const [label, insole] of guide.r || []) {
      if (typeof insole !== 'number') continue;
      if (isAvailable && !isAvailable(String(label))) continue;
      const gap = round1(insole - foot);
      let kind = null;
      if (gap >= rules.idealMin && gap <= rules.idealMax) kind = 'recommended';
      else if (gap >= rules.warnMin && gap < rules.idealMin) kind = 'acceptable';
      else if (gap > rules.idealMax && gap <= rules.warnMax) kind = 'acceptable';
      if (!kind) continue;
      const score = (kind === 'recommended' ? 0 : 10) + Math.abs(gap - rules.target);
      if (!best || score < best.score) best = { label: String(label), insole, gap, kind, score };
    }
    return best;
  };

  const h = (tag, attrs, ...children) => {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (v == null || v === false) continue;
      if (k === 'class') el.className = v;
      else if (k === 'text') el.textContent = v;
      else el.setAttribute(k, v === true ? '' : v);
    }
    for (const c of children.flat()) {
      if (c == null || c === false) continue;
      el.append(c.nodeType ? c : document.createTextNode(String(c)));
    }
    return el;
  };

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pt-PT', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  };

  // Nota: não enviar o cabeçalho Accept: application/json — nesta rota o Shopify responde 404.
  const fetchPage = async (endpoint, page) => {
    const url = endpoint + (endpoint.includes('?') ? '&' : '?') + 'page=' + page;
    const res = await fetch(url);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return JSON.parse(await res.text());
  };

  let catalogPromise = null;
  const loadCatalog = (endpoint) => {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
        if (cached && Date.now() - cached.at < CACHE_TTL) return cached.data;
      } catch (e) {}
      const first = await fetchPage(endpoint, 1);
      const data = { products: first.products.slice(), guides: Object.assign({}, first.guides) };
      const pending = [];
      for (let p = 2; p <= first.pages; p++) pending.push(p);
      const worker = async () => {
        while (pending.length) {
          const page = await fetchPage(endpoint, pending.shift());
          data.products.push(...page.products);
          Object.assign(data.guides, page.guides);
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker));
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
      } catch (e) {}
      return data;
    })().catch((err) => {
      catalogPromise = null;
      throw err;
    });
    return catalogPromise;
  };

  class PfSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      // No pop-up da página de produto esta calculadora vive dentro de <variant-picker>, que ouve TODOS os eventos 'change'
      // do seu interior e redesenha o seletor de variantes (apagando os resultados). Os eventos de formulário ficam aqui.
      // (Os listeners dos próprios campos, mais abaixo, correm antes e não são afetados.)
      this.addEventListener('change', (e) => e.stopPropagation());

      const ref = (name) => this.querySelector('[data-ref="' + name + '"]');
      this.form = ref('form');
      this.input = ref('input');
      this.errorEl = ref('error');
      this.clearBtn = ref('clear');
      this.currentEl = ref('current');
      this.filtersEl = ref('filters');
      this.brandSel = ref('brand');
      this.countEl = ref('count');
      this.resultsEl = ref('results');
      this.endpoint = this.dataset.endpoint;
      this.onlyCurrent = this.dataset.onlyCurrent === 'true';
      this.state = { foot: null, rows: [], brand: '', brandInit: false };

      try {
        const raw = this.querySelector('[data-pf-current]');
        this.current = raw ? JSON.parse(raw.textContent) : null;
      } catch (e) {
        this.current = null;
      }

      // Medida guardada (assets/pf-size-memory.js): preenche o campo e, na página de produto, mostra logo a recomendação.
      const savedFoot = window.PfFoot && PfFoot.get();
      if (savedFoot) {
        this.input.value = fmt(savedFoot);
        if (this.onlyCurrent) {
          this.state.foot = savedFoot;
          this.renderCurrent(savedFoot);
        }
      }

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.search();
      });
      this.clearBtn.addEventListener('click', () => this.reset());
      this.brandSel?.addEventListener('change', () => {
        this.state.brand = this.brandSel.value;
        this.renderResults();
      });

      // Página de produto: só interessa o modelo aberto, não há catálogo para pré-carregar.
      if (this.onlyCurrent) {
        const host = this.closest('dialog-component');
        host?.addEventListener('dialog:open', () => this.input.focus({ preventScroll: true }));
        return;
      }

      // Pré-carrega os dados quando o pop-up abre (ou em tempo livre na página), para a pesquisa ser quase instantânea.
      const warm = () => loadCatalog(this.endpoint).catch(() => {});
      const host = this.closest('dialog-component');
      if (host) {
        host.addEventListener('dialog:open', () => {
          warm();
          this.input.focus({ preventScroll: true });
        });
      } else if ('requestIdleCallback' in window) {
        requestIdleCallback(warm);
      } else {
        setTimeout(warm, 1500);
      }
    }

    parseFoot() {
      const value = parseFloat(String(this.input.value).trim().replace(',', '.'));
      return isFinite(value) ? value : null;
    }

    showError(message) {
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
      this.input.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    reset() {
      this.input.value = '';
      this.state = { foot: null, rows: [], brand: '', brandInit: false };
      this.showError('');
      this.currentEl.hidden = true;
      this.currentEl.replaceChildren();
      if (this.filtersEl) this.filtersEl.hidden = true;
      if (this.countEl) this.countEl.textContent = '';
      this.resultsEl?.replaceChildren();
      window.PfFoot?.set(null);
      this.input.focus();
    }

    async search() {
      const foot = this.parseFoot();
      if (foot === null || foot < FOOT_MIN || foot > FOOT_MAX) {
        this.showError('Indica um comprimento entre ' + FOOT_MIN + ' e ' + FOOT_MAX + ' cm.');
        return;
      }
      this.showError('');
      this.state.foot = foot;
      window.PfFoot?.set(foot);
      this.renderCurrent(foot);
      if (this.onlyCurrent) return;

      this.resultsEl.replaceChildren();
      this.filtersEl.hidden = true;
      this.countEl.textContent = '';
      this.resultsEl.setAttribute('aria-busy', 'true');
      const status = h('p', { class: 'pf-sf__status', role: 'status', text: 'Estamos a procurar os modelos ideais para o teu pé…' });
      this.resultsEl.append(status);

      let data;
      try {
        data = await loadCatalog(this.endpoint);
      } catch (e) {
        this.resultsEl.setAttribute('aria-busy', 'false');
        status.textContent = 'Não foi possível carregar os modelos. Tenta novamente daqui a pouco.';
        return;
      }
      // ignora respostas antigas se entretanto foi feita outra pesquisa
      if (this.state.foot !== foot) return;

      const rows = [];
      for (const product of data.products) {
        const guide = data.guides[product.g];
        if (!guide) continue;
        // Só conta tamanhos que o modelo tem mesmo e que estão em stock: o guia é da marca e pode incluir tamanhos
        // (ex.: 21) que este modelo não fabrica. Sem variante com esse tamanho, ou com a === false (esgotada), não é sugerido.
        const isAvailable = (label) => {
          const variant = (product.vr || []).find((v) => (v.o || []).map(String).includes(String(label)));
          return !!variant && variant.a !== false;
        };
        const rec = recommend(guide, foot, isAvailable);
        if (rec) rows.push({ product, rec });
      }
      rows.sort(
        (a, b) =>
          (a.rec.kind === b.rec.kind ? 0 : a.rec.kind === 'recommended' ? -1 : 1) ||
          a.product.v.localeCompare(b.product.v, 'pt') ||
          a.product.t.localeCompare(b.product.t, 'pt')
      );
      this.state.rows = rows;
      this.state.totalCatalog = data.products.length;

      if (!this.state.brandInit) {
        this.state.brandInit = true;
        const vendor = this.dataset.currentVendor;
        if (vendor && rows.some((r) => r.product.v === vendor)) this.state.brand = vendor;
      }
      this.buildFilters();
      this.resultsEl.setAttribute('aria-busy', 'false');
      this.renderResults();
    }

    contactLine() {
      const root = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
      return h('p', { class: 'pf-sf__current-help' }, 'Tens dúvidas? ', h('a', { href: root + '/pages/contact', text: 'Fala connosco' }), ' e ajudamos a escolher.');
    }

    renderCurrent(foot) {
      if (!this.current || !this.current.g) {
        if (this.onlyCurrent) {
          this.currentEl.replaceChildren(
            h('p', { class: 'pf-sf__current-text', text: 'Ainda não temos as medidas da palmilha deste modelo na calculadora.' }),
            this.contactLine()
          );
          this.currentEl.hidden = false;
        } else {
          this.currentEl.hidden = true;
        }
        return;
      }
      const rec = recommend(this.current.g, foot);
      const body = h('p', { class: 'pf-sf__current-text' });
      if (rec && rec.kind === 'recommended') {
        body.append('Para este modelo, o tamanho recomendado é ', h('strong', { text: rec.label }), ' — palmilha de ' + fmt(rec.insole) + ' cm, folga de ' + fmt(rec.gap) + ' cm.');
      } else if (rec) {
        body.append('Para este modelo, o tamanho ', h('strong', { text: rec.label }), ' é aceitável — palmilha de ' + fmt(rec.insole) + ' cm, mas a folga (' + fmt(rec.gap) + ' cm) fica abaixo do ideal.');
      } else if (this.onlyCurrent) {
        body.append('Este modelo não tem um tamanho com a folga adequada para este comprimento de pé.');
      } else {
        body.append('Este modelo não tem um tamanho com a folga adequada para este comprimento. Vê abaixo outros modelos.');
      }
      const parts = [h('p', { class: 'pf-sf__current-title', text: this.current.t }), body];
      if (this.onlyCurrent && (!rec || rec.kind !== 'recommended')) parts.push(this.contactLine());
      this.currentEl.replaceChildren(...parts);
      this.currentEl.hidden = false;
    }

    buildFilters() {
      const fill = (select, values, current, allLabel) => {
        select.replaceChildren(h('option', { value: '', text: allLabel }));
        for (const v of values) select.append(h('option', { value: v, text: v }));
        select.value = values.includes(current) ? current : '';
        return select.value;
      };
      const uniq = (key) => [...new Set(this.state.rows.map((r) => r.product[key]).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt'));
      this.state.brand = fill(this.brandSel, uniq('v'), this.state.brand, 'Todas as marcas');
      this.filtersEl.hidden = this.state.rows.length === 0;
    }

    renderResults() {
      const { rows, brand } = this.state;
      const list = rows.filter((r) => (!brand || r.product.v === brand));
      this.resultsEl.replaceChildren();

      if (!this.state.totalCatalog) {
        this.countEl.textContent = '';
        this.resultsEl.append(h('p', { class: 'pf-sf__status', text: 'A calculadora está a ser preparada. Volta em breve.' }));
        return;
      }
      if (!list.length) {
        this.countEl.textContent = '';
        this.resultsEl.append(h('p', { class: 'pf-sf__status', text: 'Não encontrámos modelos compatíveis com este comprimento' + (brand ? ' e filtros escolhidos.' : '.') }));
        return;
      }
      this.countEl.textContent = list.length + (list.length === 1 ? ' modelo compatível' : ' modelos compatíveis');
      this.resultsEl.append(...list.map((r, i) => this.card(r, i)));
    }

    card({ product, rec }, index) {
      const variant = (product.vr || []).find((v) => (v.o || []).map(String).includes(rec.label));
      const url = '/products/' + product.h + (variant ? '?variant=' + variant.id : '');
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const href = root.replace(/\/$/, '') + url;
      const media = h('a', { class: 'pf-sf__card-media', href, tabindex: '-1', 'aria-hidden': 'true' });
      if (product.i) media.append(h('img', { src: product.i, alt: '', width: 400, height: 400, loading: 'lazy' }));
      const price = h('p', { class: 'pf-sf__price', text: money(product.p) });
      if (product.cp && product.cp > product.p) price.append(h('s', { text: money(product.cp) }));

      const card = h(
        'article',
        { class: 'pf-sf__card', style: 'animation-delay:' + Math.min(index, 12) * 25 + 'ms' },
        media,
        h(
          'div',
          { class: 'pf-sf__card-body' },
          h('p', { class: 'pf-sf__vendor', text: product.v }),
          h('h3', { class: 'pf-sf__title' }, h('a', { href, text: product.t })),
          h('span', { class: 'pf-sf__badge', 'data-kind': rec.kind, text: rec.kind === 'recommended' ? 'Recomendado' : 'Aceitável' }),
          h('p', { class: 'pf-sf__size' }, h('span', { text: 'Tamanho recomendado' }), h('strong', { text: rec.label })),
          h(
            'dl',
            { class: 'pf-sf__meta' },
            h('div', {}, h('dt', { text: 'Palmilha' }), h('dd', { text: fmt(rec.insole) + ' cm' })),
            h('div', {}, h('dt', { text: 'Folga' }), h('dd', { text: fmt(rec.gap) + ' cm' }))
          ),
          variant && variant.a === false ? h('p', { class: 'pf-sf__stock', text: 'Tamanho esgotado' }) : null,
          price,
          h('a', { class: 'btn btn--secondary btn--medium', href, text: 'Ver produto' })
        )
      );
      return card;
    }
  }

  customElements.define('pf-size-finder', PfSizeFinder);
})();
